import { ReactNode, Ref, useEffect, useRef, useState } from 'react';
import styled, { css } from 'styled-components';

import Button from 'components/shared/Button';
import Foldout from 'components/shared/Foldout';
import Menu, { useMenuContext } from 'components/shared/menu';
import { mobileTouchExpand, mobileTouchTarget } from 'components/shared/responsiveness/touchTarget';
import useIsMobile from 'hooks/useIsMobile';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';

import { DemographicConflict } from './demographicConflicts';

// A Patient Demographics field the packet disagrees with (OLIO-3252, Max's design): the field is
// highlighted, and clicking it shows what the packet said beside the value in use, with Dismiss and
// "Use this value". Built on the shared Menu — click to open, closes on an outside click, portalled
// so the card's overflow can't clip it. The hover-only Tooltip can't hold buttons.
//
// Without edit access (or on a closed referral) the same panel opens read-only: seeing that the
// packet disagrees is still worth knowing, even for someone who can't act on it.

interface Props {
  conflict: DemographicConflict;
  // The cell's own label ("Date of Birth", "Member ID (Secondary)"). Only the sheet shows it: the
  // pop-up is anchored to the field, which says what it is, while a sheet at the bottom of the
  // screen is detached from it. Taken from the card rather than derived here, so one place decides
  // what a field is called.
  label: string;
  // The value in use and the packet's reading, already rendered the way the card shows them (a DOB
  // as a date; an SSN through its reveal control, since two masked numbers can share a last four).
  current: ReactNode;
  packet: ReactNode;
  // Where the packet's reading came from, as citation badges. Shown whatever the card's sources toggle
  // says, since the panel is opened on purpose.
  packetSources?: ReactNode;
  canResolve: boolean;
  saving: boolean;
  onUse: (conflict: DemographicConflict) => void;
  onDismiss: (conflict: DemographicConflict) => void;
  // The card's own rendering of the value in use — what sits inside the highlight.
  children: ReactNode;
}

export default function DemographicConflictPopover({ children, ...props }: Props) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);
  const sheetRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // What the Menu gives the pop-up for free, written out for the sheet: an outside click closes
  // it, and so does Escape. The sheet is not modal, so there is no backdrop to catch either one.
  // Crossing back above the breakpoint takes the sheet's markup away with it, so the open flag has
  // to go too — otherwise widening past 800px and narrowing again re-opens it by itself.
  useEffect(() => {
    if (!isMobile) {
      setOpen(false);
      return;
    }
    if (!open) return;

    const onClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (sheetRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('click', onClick);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [isMobile, open]);

  const trigger = (onClick?: () => void, ref?: Ref<HTMLButtonElement>) => (
    <Highlight
      ref={ref}
      type='button'
      data-testid={`conflict-${props.conflict.key}`}
      aria-haspopup='dialog'
      onClick={onClick}>
      {children}
    </Highlight>
  );

  // A sheet from the bottom on a phone rather than the anchored pop-up. The pop-up is capped at
  // 256px against a card with about 343px to give, and it has to hold two readings, their document
  // badges and two buttons — worst case an SSN against an SSN, where the two differ only in the
  // last four and each sits behind a reveal control. Anchoring also stops paying for itself here:
  // on a desktop the pop-up sits beside the value it is about, on a phone it covers it.
  if (isMobile) {
    return (
      <>
        {trigger(() => setOpen((wasOpen) => !wasOpen), triggerRef)}
        <Foldout
          position='bottom'
          open={open}
          setOpen={setOpen}
          // Not modal. A packet that disagrees about a date of birth usually disagrees about a
          // member ID as well, and a backdrop over the card makes every move between two
          // conflicting fields two taps — one to dismiss the backdrop, one to open the next. The
          // desktop pop-up has no backdrop either.
          modal={false}
          data-testid={`conflict-sheet-${props.conflict.key}`}>
          <Sheet ref={sheetRef} role='dialog' aria-label={props.label}>
            <SheetTitle>{props.label}</SheetTitle>
            <Panel {...props} $sheet onClose={() => setOpen(false)} />
          </Sheet>
        </Foldout>
      </>
    );
  }

  return (
    <Menu>
      <Menu.Trigger>{trigger()}</Menu.Trigger>
      <Menu.Content style={{ marginTop: 4 }}>
        <MenuPanel {...props} />
      </Menu.Content>
    </Menu>
  );
}

// The pop-up path closes itself through the menu's own context; the sheet owns its open state and
// passes a closer in. One Panel either way, so the two cannot drift apart.
function MenuPanel(props: Omit<Props, 'children'>) {
  const { toggle } = useMenuContext();
  return <Panel {...props} onClose={() => toggle(false)} />;
}

function Panel({
  conflict,
  current,
  packet,
  packetSources,
  canResolve,
  saving,
  onUse,
  onDismiss,
  onClose,
  $sheet,
}: Omit<Props, 'children'> & { onClose: () => void; $sheet?: boolean }) {
  const answer = (respond: (conflict: DemographicConflict) => void) => () => {
    respond(conflict);
    onClose();
  };

  // Both paths' guard. The shared Menu closes on any document click outside its TRIGGER, and on a
  // desktop this panel is portalled away from the trigger — so a click inside it would close it.
  // The sheet tests the click against its own box instead, which covers most of it but NOT the
  // SSN's reveal eye: revealing replaces the button that was clicked, so by the time the document
  // listener runs the target is detached and contained by nothing. Stopping the click here is what
  // keeps either open for its own controls; the two answers close it themselves.
  return (
    <Body $sheet={$sheet} data-testid={`conflict-panel-${conflict.key}`} onClick={(event) => event.stopPropagation()}>
      <Reading>
        <Caption>Current</Caption>
        <Valued>
          <BodySmall as='div' data-testid='conflict-current-value'>
            {current}
          </BodySmall>
        </Valued>
      </Reading>
      <Reading>
        <Caption>Newly Extracted</Caption>
        <Valued>
          <BodySmall as='div' data-testid='conflict-packet-value'>
            {packet}
          </BodySmall>
          {packetSources}
        </Valued>
      </Reading>
      {canResolve && (
        <Buttons $sheet={$sheet}>
          <Button
            size={$sheet ? 'small' : 'xsmall'}
            variant='outline-gray'
            disabled={saving}
            onClick={answer(onDismiss)}>
            Dismiss
          </Button>
          <Button size={$sheet ? 'small' : 'xsmall'} disabled={saving} onClick={answer(onUse)}>
            Use New Value
          </Button>
        </Buttons>
      )}
    </Body>
  );
}

// Amber like the other "needs a look" marks on this page (FacilityMappingReview's unresolved box);
// text stays black on it, since yellow100 text fails contrast.
const Highlight = styled.button`
  all: unset;
  box-sizing: border-box;
  cursor: pointer;
  display: block;
  margin: -2px -6px;
  padding: 1px 5px;
  border: 1px solid ${colors.yellow100};
  border-radius: 4px;
  background: ${colors.yellow5};

  &:hover,
  &:focus-visible {
    background: ${colors.yellow10};
  }

  /* The only way into the sheet, and a one-line value inside a card row is about 20px tall. */
  ${mobileTouchExpand}
`;

const Body = styled.div<{ $sheet?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px;
  min-width: 200px;

  /* In the sheet the width comes from the screen, so the pop-up's minimum would only fight it. */
  ${({ $sheet }) =>
    $sheet &&
    css`
      min-width: 0;
      gap: 16px;
      padding: 0;
    `}
`;

const Reading = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const Valued = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Caption = styled(BodySmall)`
  color: ${colors.black50};
  font-size: 11px;
`;

const Buttons = styled.div<{ $sheet?: boolean }>`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
  padding-top: 4px;

  button {
    padding: 4px 16px;
  }

  /* Full width and side by side in the sheet: there is room for both, and a pair of small buttons
     in the bottom-right corner is the hardest part of a phone to reach. */
  ${({ $sheet }) =>
    $sheet &&
    css`
      padding-top: 8px;

      button {
        flex: 1;
        padding: 8px 16px;
        ${mobileTouchTarget}
      }
    `}
`;

// The sheet's own chrome. The pop-up needs no heading — it is anchored to the field it is about,
// which says what it is; detached at the bottom of the screen it does not, so the field is named.
const Sheet = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  /* Foldout already pays the bottom inset on the sheet itself; adding it here would pay it twice. */
  padding: 20px 16px;
`;

const SheetTitle = styled(BodySmallBold)`
  font-size: 16px;
  color: ${colors.black100};
`;
